import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, DollarSign, Coins } from 'lucide-react';
import { GlassCard } from './ui/GlassCard';
interface DonateModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const PRESET_AMOUNTS = [5, 10, 25, 50];
export function DonateModal({
  isOpen,
  onClose
}: DonateModalProps) {
  const [selectedAmount, setSelectedAmount] = useState(10);
  const [customAmount, setCustomAmount] = useState('');
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  const handleDonate = (method: 'stripe' | 'paypal' | 'crypto') => {
    const amount = customAmount || selectedAmount;
    // TODO: Replace these placeholder URLs with your actual payment links
    const links = {
      stripe: `https://donate.stripe.com/test_xxxxx?amount=${amount * 100}`,
      paypal: `https://www.paypal.com/donate?amount=${amount}`,
      crypto: 'https://commerce.coinbase.com/checkout/xxxxx'
    };
    window.open(links[method], '_blank');
  };
  if (!isOpen) return null;
  const modalContent = <AnimatePresence data-id="element-215">
      <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" data-id="element-216">
        {/* Dark Blurred Overlay */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-black/90 backdrop-blur-xl" onClick={onClose} data-id="element-217" />

        {/* Modal Content */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="relative w-full max-w-lg" onClick={e => e.stopPropagation()} data-id="element-218">
          <GlassCard className="relative border-white/10" data-id="element-219">
            <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all text-gray-400 hover:text-white z-10" data-id="element-220">
              <X size={20} data-id="element-221" />
            </button>

            <div className="p-8" data-id="element-222">
              {/* Header */}
              <div className="text-center mb-8" data-id="element-223">
                <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: 0.1,
                type: 'spring',
                stiffness: 200
              }} className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border border-cyan-500/30 mb-4" data-id="element-224">
                  <span className="text-3xl" data-id="element-225">💝</span>
                </motion.div>
                <h2 className="text-3xl font-bold text-white mb-2 font-special" data-id="element-226">
                  Support Faind
                </h2>
                <p className="text-gray-400 text-sm" data-id="element-227">
                  Help us keep the directory free and updated daily with the
                  latest AI tools
                </p>
              </div>

              {/* Amount Selection */}
              <div className="mb-6" data-id="element-228">
                <label className="block text-sm font-medium text-gray-300 mb-3" data-id="element-229">
                  Choose an amount
                </label>
                <div className="grid grid-cols-4 gap-2 mb-3" data-id="element-230">
                  {PRESET_AMOUNTS.map(amount => <button key={amount} onClick={() => {
                  setSelectedAmount(amount);
                  setCustomAmount('');
                }} className={`
                        py-3 rounded-lg font-bold transition-all border
                        ${selectedAmount === amount && !customAmount ? 'bg-gradient-to-r from-cyan-500 to-purple-500 text-white border-transparent' : 'bg-white/5 text-gray-300 border-white/10 hover:bg-white/10 hover:border-white/20'}
                      `} data-id="element-231">
                      ${amount}
                    </button>)}
                </div>
                <input type="number" placeholder="Custom amount" value={customAmount} onChange={e => setCustomAmount(e.target.value)} className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 transition-all" data-id="element-232" />
              </div>

              {/* Payment Methods */}
              <div className="space-y-3" data-id="element-233">
                {/* Stripe */}
                <motion.button whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} onClick={() => handleDonate('stripe')} className="w-full p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-cyan-500/30 transition-all group" data-id="element-234">
                  <div className="flex items-center gap-4" data-id="element-235">
                    <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 group-hover:border-cyan-500/50 transition-all" data-id="element-236">
                      <CreditCard size={24} className="text-cyan-400" data-id="element-237" />
                    </div>
                    <div className="flex-1 text-left" data-id="element-238">
                      <div className="font-bold text-white mb-0.5" data-id="element-239">
                        Credit Card / Apple Pay
                      </div>
                      <div className="text-xs text-gray-400" data-id="element-240">
                        Secure payment via Stripe
                      </div>
                    </div>
                    <div className="text-cyan-400 font-bold" data-id="element-241">→</div>
                  </div>
                </motion.button>

                {/* PayPal */}
                <motion.button whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} onClick={() => handleDonate('paypal')} className="w-full p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-blue-500/30 transition-all group" data-id="element-242">
                  <div className="flex items-center gap-4" data-id="element-243">
                    <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/30 group-hover:border-blue-500/50 transition-all" data-id="element-244">
                      <DollarSign size={24} className="text-blue-400" data-id="element-245" />
                    </div>
                    <div className="flex-1 text-left" data-id="element-246">
                      <div className="font-bold text-white mb-0.5" data-id="element-247">PayPal</div>
                      <div className="text-xs text-gray-400" data-id="element-248">
                        Quick checkout with PayPal
                      </div>
                    </div>
                    <div className="text-blue-400 font-bold" data-id="element-249">→</div>
                  </div>
                </motion.button>

                {/* Crypto */}
                <motion.button whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} onClick={() => handleDonate('crypto')} className="w-full p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-orange-500/30 transition-all group" data-id="element-250">
                  <div className="flex items-center gap-4" data-id="element-251">
                    <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500/20 to-yellow-500/20 border border-orange-500/30 group-hover:border-orange-500/50 transition-all" data-id="element-252">
                      <Coins size={24} className="text-orange-400" data-id="element-253" />
                    </div>
                    <div className="flex-1 text-left" data-id="element-254">
                      <div className="font-bold text-white mb-0.5" data-id="element-255">
                        Cryptocurrency
                      </div>
                      <div className="text-xs text-gray-400" data-id="element-256">
                        Bitcoin, Ethereum & more
                      </div>
                    </div>
                    <div className="text-orange-400 font-bold" data-id="element-257">→</div>
                  </div>
                </motion.button>
              </div>

              {/* Footer note */}
              <p className="text-center text-xs text-gray-500 mt-6" data-id="element-258">
                All donations are processed securely through trusted payment
                providers
              </p>
            </div>
          </GlassCard>
        </motion.div>
      </div>
    </AnimatePresence>;
  return createPortal(modalContent, document.body);
}